import { 
  collection, 
  doc as firebaseDoc, 
  getDocs, 
  getDoc,
  setDoc, 
  updateDoc, 
  serverTimestamp,
  Timestamp,
  runTransaction,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { CoinHistory } from '../types/alarm';
import { 
  MembersCoinDocument, 
  StudentCoinData, 
  UndoCoinInfo,
  StudentCoinSummary
} from '../types/coinManagement';
import { CoinHistoryEntry } from '../types/index';
import {
  CoinManagementPartitionV2,
  StudentCoinInfoV2
} from '../types/index';

// ===== 배열 ↔ 중첩 객체 변환 유틸리티 =====

/** coinsHistory 엔트리 키 (earnedAt 기준, V2 객체 저장용) */
export function getCoinHistoryKey(entry: { earnedAt: Date | Timestamp | any }): string {
  if (!entry?.earnedAt) return `_${Date.now()}`;
  const e = entry.earnedAt;
  if (e && typeof e.toMillis === 'function') {
    const ms = e.toMillis();
    const ns = (e as any).nanoseconds ?? 0;
    return ns ? `${ms}_${ns}` : `${ms}`;
  }
  if (e instanceof Date) return `${e.getTime()}`;
  if (typeof e?.seconds === 'number') {
    const ms = e.seconds * 1000;
    const ns = e.nanoseconds ?? 0;
    return ns ? `${ms}_${ns}` : `${ms}`;
  }
  if (typeof e === 'number') return `${e}`;
  return `_${Date.now()}`;
}

function earnedAtMs(h: any): number {
  if (!h?.earnedAt) return 0;
  const e = h.earnedAt;
  if (e instanceof Date) return e.getTime();
  if (typeof e?.toMillis === 'function') return e.toMillis();
  if (typeof e?.toDate === 'function') return e.toDate().getTime();
  if (typeof e?.seconds === 'number') return e.seconds * 1000;
  return 0;
}

function normalizeCoinHistoryEntry(h: any): CoinHistoryEntry {
  return {
    earnedCoin: h?.earnedCoin ?? 0,
    earnedCoinMemo: h?.earnedCoinMemo ?? '',
    earnedAt: h?.earnedAt instanceof Date ? h.earnedAt : (h?.earnedAt?.toDate?.() || new Date(h?.earnedAt)),
    coinType: h?.coinType || 'General'
  };
}

/** coinsHistory raw(배열 또는 V2 객체) → 정렬된 배열 (듀얼 읽기) */
function coinsHistoryRawToArray(raw: any): CoinHistoryEntry[] {
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.map(normalizeCoinHistoryEntry).sort((a, b) => earnedAtMs(a) - earnedAtMs(b));
  }
  if (typeof raw === 'object') {
    return Object.values(raw).map(normalizeCoinHistoryEntry).sort((a, b) => earnedAtMs(a) - earnedAtMs(b));
  }
  return [];
}

/** coinsHistory 배열 → V2 객체 { [key]: entry } (저장용) */
function coinsHistoryArrayToObject(arr: CoinHistoryEntry[]): Record<string, any> {
  const obj: Record<string, any> = {};
  for (const h of arr) {
    obj[getCoinHistoryKey(h)] = h;
  }
  return obj;
}

/**
 * 배열 형태의 students를 중첩 객체로 변환
 */
function studentsArrayToObject(
  arr: StudentCoinData[]
): { [studentId: string]: StudentCoinInfoV2 } {
  const obj: { [studentId: string]: StudentCoinInfoV2 } = {};
  for (const student of arr) {
    const { studentId, ...rest } = student;
    const historyArr = coinsHistoryRawToArray(rest.coinsHistory);
    obj[studentId] = {
      studentName: rest.studentName,
      studentNumber: rest.studentNumber,
      coins: rest.coins,
      coinsHistory: historyArr.map(normalizeCoinHistoryEntry),
      totalEarned: rest.totalEarned || 0,
      totalUsed: rest.totalUsed || 0
    };
  }
  return obj;
}

/**
 * 중첩 객체 형태의 students를 배열로 변환 (하위 호환성, coinsHistory 듀얼 읽기)
 */
function studentsObjectToArray(
  obj: { [studentId: string]: StudentCoinInfoV2 }
): StudentCoinData[] {
  return Object.entries(obj).map(([studentId, info]) => ({
    studentId,
    studentName: info.studentName,
    studentNumber: info.studentNumber,
    coins: info.coins,
    coinsHistory: coinsHistoryRawToArray(info.coinsHistory),
    totalEarned: info.totalEarned,
    totalUsed: info.totalUsed
  }));
}

/**
 * 데이터가 배열인지 객체인지 확인
 */
function isArrayData(data: any): boolean {
  return Array.isArray(data);
}

export class CoinManagementService {
  private userId: string;
  private classId: string;
  private memberGroup: string;
  private lastGrantedCoins: UndoCoinInfo | null = null;
  private readonly maxDocumentSize = 900000; // 900KB (1MB 제한의 90%)

  constructor(userId: string, classId: string, memberGroup?: string) {
    this.userId = userId;
    this.classId = classId;

    // 그룹 파라미터가 없으면(localStorage 기반) 현재 선택된 그룹 사용
    let resolvedGroup = memberGroup;
    if (typeof resolvedGroup === 'undefined') {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const key = `selected_member_group_${userId}_${classId}`;
          const saved = window.localStorage.getItem(key);
          if (saved) {
            resolvedGroup = saved;
          }
        }
      } catch {
        // ignore
      }
    }
    this.memberGroup = (resolvedGroup || 'A').toString().trim().toUpperCase();
  }

  /**
   * 그룹별 코인 컬렉션 경로 반환
   * - A 그룹: ClassMembersCoin
   * - B 그룹: ClassMembersCoin_B
   * - C 그룹: ClassMembersCoin_C
   */
  private getCollectionPath(): string {
    const basePath = `users/${this.userId}/classes/${this.classId}`;
    if (this.memberGroup && this.memberGroup !== 'A') {
      return `${basePath}/ClassMembersCoin_${this.memberGroup}`;
    }
    return `${basePath}/ClassMembersCoin`;
  }

  private getMembersCoinDocId(partitionNumber: number): string {
    if (this.memberGroup === 'A') {
      return `MembersCoin-${partitionNumber}`;
    }
    return `MembersCoin${this.memberGroup}-${partitionNumber}`;
  }

  private parseMembersCoinPartitionNumber(docId: string): number | null {
    const regex =
      this.memberGroup === 'A'
        ? /^MembersCoin-(\d+)$/
        : new RegExp(`^MembersCoin${this.memberGroup}-(\\d+)$`);
    const match = docId.match(regex);
    if (!match) return null;
    return parseInt(match[1], 10);
  }

  private isCurrentGroupMembersCoinDocId(docId: string): boolean {
    return this.parseMembersCoinPartitionNumber(docId) !== null;
  }

  /**
   * 문서 크기 계산
   */
  private calculateDocumentSize(doc: any): number {
    return JSON.stringify(doc).length;
  }

  /**
   * 모든 MembersCoin 문서 조회
   */
  // 코인 문서 캐시 (메모리 + localStorage)
  private coinDocumentsCache: { data: MembersCoinDocument[], timestamp: number } | null = null;
  private coinDocumentsCacheV2: { data: CoinManagementPartitionV2[], timestamp: number } | null = null;
  private readonly COIN_CACHE_TTL = 5 * 60 * 1000; // 5분

  // 캐시 무효화 (코인 데이터 변경 시 호출)
  invalidateCoinDocumentsCache() {
    this.coinDocumentsCache = null;
    this.coinDocumentsCacheV2 = null;
    console.log('🗑️ 코인 문서 캐시 무효화 완료');
  }

  /**
   * 모든 MembersCoin 문서 조회 (V2: 배열/중첩객체 듀얼 지원)
   */
  async getAllMembersCoinDocuments(): Promise<MembersCoinDocument[]> {
    try {
      // 캐시 확인
      const now = Date.now();
      if (this.coinDocumentsCache && now - this.coinDocumentsCache.timestamp < this.COIN_CACHE_TTL) {
        console.log('💾 코인 문서 캐시 사용 (최신):', { count: this.coinDocumentsCache.data.length, age: Math.round((now - this.coinDocumentsCache.timestamp) / 1000), unit: '초' });
        return this.coinDocumentsCache.data;
      }

      console.log('🔄 코인 문서 캐시 없음 또는 오래됨, DB에서 로드');
      const collectionRef = collection(db, this.getCollectionPath());
      const snapshot = await getDocs(collectionRef);

      const documents: MembersCoinDocument[] = [];
      snapshot.forEach(doc => {
        // 다른 멤버 그룹 문서 제외
        if (!this.isCurrentGroupMembersCoinDocId(doc.id)) return;

        const data = doc.data();
        const partitionNumber = this.parseMembersCoinPartitionNumber(doc.id) || 0;

        // students 필드 처리 (배열/객체 듀얼 지원)
        let studentsArray: StudentCoinData[] = [];
        
        if (data.students) {
          if (isArrayData(data.students)) {
            studentsArray = data.students.map((s: any) => ({
              ...s,
              coinsHistory: coinsHistoryRawToArray(s.coinsHistory)
            }));
          } else {
            studentsArray = studentsObjectToArray(data.students);
          }
        }

        documents.push({
          id: doc.id,
          classId: data.classId || this.classId,
          documentNumber: data.documentNumber || partitionNumber,
          students: studentsArray,
          totalStudents: data.totalStudents || 0,
          totalSize: data.totalSize,
          lastUpdated: data.lastUpdated?.toDate() || new Date()
        });
      });

      // 문서 번호 순으로 정렬
      const sortedDocuments = documents.sort((a, b) => a.documentNumber - b.documentNumber);

      // 캐시 저장
      this.coinDocumentsCache = {
        data: sortedDocuments,
        timestamp: now
      };
      console.log('💾 코인 문서 캐시 저장 완료:', { count: sortedDocuments.length });

      return sortedDocuments;
    } catch (error) {
      console.error('❌ MembersCoin 문서 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 모든 MembersCoin 문서 조회 (V2: 중첩 객체 형식으로 반환)
   */
  async getAllMembersCoinDocumentsV2(): Promise<CoinManagementPartitionV2[]> {
    try {
      // 캐시 확인
      const now = Date.now();
      if (this.coinDocumentsCacheV2 && now - this.coinDocumentsCacheV2.timestamp < this.COIN_CACHE_TTL) {
        console.log('💾 코인 문서 V2 캐시 사용 (최신):', { count: this.coinDocumentsCacheV2.data.length, age: Math.round((now - this.coinDocumentsCacheV2.timestamp) / 1000), unit: '초' });
        return this.coinDocumentsCacheV2.data;
      }

      console.log('🔄 코인 문서 V2 캐시 없음 또는 오래됨, DB에서 로드');
      const collectionRef = collection(db, this.getCollectionPath());
      const snapshot = await getDocs(collectionRef);

      const documents: CoinManagementPartitionV2[] = [];
      snapshot.forEach(doc => {
        // 다른 멤버 그룹 문서 제외
        if (!this.isCurrentGroupMembersCoinDocId(doc.id)) return;

        const data = doc.data();
        const partitionNumber = this.parseMembersCoinPartitionNumber(doc.id) || 0;

        // students 필드 처리 (배열/객체 듀얼 지원)
        let studentsObj: { [studentId: string]: StudentCoinInfoV2 } = {};
        
        if (data.students) {
          if (isArrayData(data.students)) {
            studentsObj = studentsArrayToObject(data.students);
          } else {
            studentsObj = { ...data.students };
            for (const sid of Object.keys(studentsObj)) {
              studentsObj[sid] = {
                ...studentsObj[sid],
                coinsHistory: coinsHistoryRawToArray(studentsObj[sid].coinsHistory)
              };
            }
          }
        }

        documents.push({
          id: doc.id,
          classId: data.classId || this.classId,
          documentNumber: data.documentNumber || partitionNumber,
          students: studentsObj,
          totalStudents: data.totalStudents || 0,
          totalSize: data.totalSize || 0,
          lastUpdated: data.lastUpdated?.toDate() || new Date()
        });
      });

      // 문서 번호 순으로 정렬
      const sortedDocuments = documents.sort((a, b) => a.documentNumber - b.documentNumber);

      // 캐시 저장
      this.coinDocumentsCacheV2 = {
        data: sortedDocuments,
        timestamp: now
      };
      console.log('💾 코인 문서 V2 캐시 저장 완료:', { count: sortedDocuments.length });

      return sortedDocuments;
    } catch (error) {
      console.error('❌ MembersCoin 문서 V2 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 새 MembersCoin 문서 생성 (V2: 중첩 객체 형식)
   */
  async createNewMembersCoinDocument(): Promise<string> {
    try {
      const documents = await this.getAllMembersCoinDocuments();
      const nextNumber = documents.length > 0 
        ? Math.max(...documents.map(d => d.documentNumber)) + 1 
        : 1;
      const docId = this.getMembersCoinDocId(nextNumber);
      
      console.log(`📝 새 코인 문서 생성: ${docId} (그룹: ${this.memberGroup})`);

      // V2: 빈 객체로 시작 (멤버는 코인 지급 시점에 동적으로 추가)
      const initialStudents: { [studentId: string]: StudentCoinInfoV2 } = {};

      const newDoc = {
        id: docId,
        classId: this.classId,
        documentNumber: nextNumber,
        students: initialStudents,
        totalStudents: 0,
        totalSize: 0,
        lastUpdated: serverTimestamp()
      };

      const docRef = firebaseDoc(db, this.getCollectionPath(), docId);
      await setDoc(docRef, newDoc);

      console.log(`✅ 새 MembersCoin 문서 생성: ${docId} (번호: ${nextNumber}, V2 형식)`);

      // 캐시 무효화 (새 문서가 생성되었으므로)
      this.invalidateCoinDocumentsCache();

      return docId;
    } catch (error) {
      console.error('❌ MembersCoin 문서 생성 실패:', error);
      throw error;
    }
  }

  /**
   * 기존 문서의 멤버 배열 초기화
   */
  private async initializeStudentsInDocument(docId: string): Promise<void> {
    try {
      console.log(`📝 문서 ${docId}의 멤버 배열 초기화 시작`);
      
      const classMembers = await this.loadClassMembers();
      console.log(`📝 조회된 클래스 멤버 수: ${classMembers.length}`);
      
      const initialStudents: StudentCoinData[] = classMembers.map(member => ({
        studentId: member.studentId,
        studentName: member.studentName,
        studentNumber: member.studentNumber,
        coins: 0,
        coinsHistory: [],
        totalEarned: 0,
        totalUsed: 0
      }));
      
      const docRef = firebaseDoc(db, this.getCollectionPath(), docId);
      await updateDoc(docRef, {
        students: initialStudents,
        totalStudents: initialStudents.length,
        lastUpdated: serverTimestamp()
      });
      
      console.log(`✅ 문서 ${docId}의 멤버 배열 초기화 완료`);
    } catch (error) {
      console.error(`❌ 문서 ${docId}의 멤버 배열 초기화 실패:`, error);
      throw error;
    }
  }

  /**
   * 클래스 멤버들 조회
   */
  private async loadClassMembers(): Promise<any[]> {
    try {
      console.log(`🔍 클래스 멤버 조회 시작: ${this.classId}`);
      
      const { ClassMembersPartitionService } = await import('./classMembersPartitionService');
      const partitionService = new ClassMembersPartitionService(this.userId, this.classId, this.memberGroup);
      const members = await partitionService.loadAllMembers();
      
      const formattedMembers = members.map((member: any) => ({
        studentId: member.id,
        studentName: member.name || '',
        studentNumber: member.number || ''
      }));
      
      console.log(`🔍 클래스 멤버 조회 완료: ${formattedMembers.length}명`);
      return formattedMembers;
    } catch (error) {
      console.error('❌ 클래스 멤버 조회 실패:', error);
      return [];
    }
  }

  /**
   * 특정 멤버의 코인 데이터 조회
   */
  async getStudentCoinData(studentId: string): Promise<StudentCoinData | null> {
    try {
      const documents = await this.getAllMembersCoinDocuments();
      
      for (const doc of documents) {
        const student = doc.students.find(s => s.studentId === studentId);
        if (student) {
          return student;
        }
      }

      return null;
    } catch (error) {
      console.error('❌ 멤버 코인 데이터 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 코인 잔액 조회
   */
  async getStudentCoins(studentId: string): Promise<number> {
    const studentData = await this.getStudentCoinData(studentId);
    return studentData?.coins || 0;
  }

  /**
   * 특정 멤버의 코인 데이터 조회 (V2: 듀얼 읽기 지원)
   */
  async loadStudentCoins(studentId: string): Promise<StudentCoinData | null> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const documentsV2 = await this.getAllMembersCoinDocumentsV2();
      
      for (const doc of documentsV2) {
        const studentInfo = doc.students[studentId];
        if (studentInfo) {
          // 중첩 객체를 배열 형식으로 변환하여 반환 (하위 호환성)
          return {
            studentId,
            studentName: studentInfo.studentName,
            studentNumber: studentInfo.studentNumber,
            coins: studentInfo.coins,
            coinsHistory: studentInfo.coinsHistory,
            totalEarned: studentInfo.totalEarned,
            totalUsed: studentInfo.totalUsed
          };
        }
      }

      return null;
    } catch (error) {
      console.error('❌ 멤버 코인 데이터 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 코인 데이터 조회 (V2: 중첩 객체 형식으로 반환)
   */
  async loadStudentCoinsV2(studentId: string): Promise<StudentCoinInfoV2 | null> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const documentsV2 = await this.getAllMembersCoinDocumentsV2();
      
      for (const doc of documentsV2) {
        const studentInfo = doc.students[studentId];
        if (studentInfo) {
          return studentInfo;
        }
      }

      return null;
    } catch (error) {
      console.error('❌ 멤버 코인 데이터 V2 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 멤버의 코인 히스토리 조회
   */
  async getStudentCoinHistory(studentId: string): Promise<CoinHistory[]> {
    const studentData = await this.getStudentCoinData(studentId);
    return studentData?.coinsHistory || [];
  }

  /**
   * 모든 멤버의 코인 데이터 조회
   */
  async getAllStudentsCoinData(): Promise<StudentCoinData[]> {
    try {
      const documents = await this.getAllMembersCoinDocuments();
      const allStudents: StudentCoinData[] = [];
      
      for (const doc of documents) {
        allStudents.push(...doc.students);
      }
      
      return allStudents;
    } catch (error) {
      console.error('❌ 모든 멤버 코인 데이터 조회 실패:', error);
      return [];
    }
  }

  /**
   * 멤버에게 코인 지급 (V2: 부분 업데이트 - dot notation)
   */
  async grantCoinsToStudent(
    studentId: string, 
    amount: number, 
    memo: string = '코인 지급',
    coinType: 'General' | 'Levelup' | 'Store' | 'Assignment' = 'General'
  ): Promise<void> {
    try {
      console.log(`💰 코인 지급 시작: ${studentId}, ${amount}코인, 타입: ${coinType}`);

      // V2: 중첩 객체 형식으로 문서 조회
      let documentsV2 = await this.getAllMembersCoinDocumentsV2();

      // 문서가 없으면 첫 번째 문서 생성 (빈 문서로 시작)
      if (documentsV2.length === 0) {
        console.log('📝 MembersCoin 문서가 없음. 새 문서 생성 중...');
        await this.createNewMembersCoinDocument();
        documentsV2 = await this.getAllMembersCoinDocumentsV2();
      }

      const now = new Date();
      const coinHistoryEntry: CoinHistoryEntry = {
        earnedCoin: amount,
        earnedCoinMemo: memo,
        earnedAt: now,
        coinType: coinType || 'General'
      };
      const entryForFirestore = { ...coinHistoryEntry, earnedAt: Timestamp.fromDate(now) };
      const historyKey = getCoinHistoryKey(entryForFirestore);

      let targetDocId = '';
      let targetDocV2: CoinManagementPartitionV2 | null = null;

      for (const doc of documentsV2) {
        if (doc.students[studentId]) {
          targetDocId = doc.id;
          targetDocV2 = doc;
          break;
        }
      }

      if (!targetDocId) {
        const lastDoc = documentsV2[documentsV2.length - 1];
        targetDocId = lastDoc?.id || '';
        targetDocV2 = lastDoc || null;
      }

      if (!targetDocId || !targetDocV2) {
        throw new Error('MembersCoin 문서를 찾을 수 없습니다.');
      }

      // 새 학생 추가 시 클래스 멤버 정보 미리 로드
      let studentName = '';
      let studentNumber = '';
      const existingStudentInDoc = targetDocV2.students[studentId];
      if (!existingStudentInDoc) {
        try {
          const classMembers = await this.loadClassMembers();
          const member = classMembers.find((m: any) => m.studentId === studentId);
          if (member) {
            studentName = member.studentName || '';
            studentNumber = member.studentNumber || '';
          }
        } catch (error) {
          console.warn(`클래스 멤버 정보 로드 실패 (무시): ${studentId}`, error);
        }
      }

      const docRef = firebaseDoc(db, this.getCollectionPath(), targetDocId);

      await runTransaction(db, async (tx) => {
        const freshSnap = await tx.get(docRef);
        if (!freshSnap.exists()) {
          throw new Error('MembersCoin 문서를 찾을 수 없습니다.');
        }
        const freshData = freshSnap.data() as any;
        
        // students 필드 처리 (배열/객체 듀얼 지원)
        let studentsObj: { [studentId: string]: StudentCoinInfoV2 } = {};
        if (freshData.students) {
          if (isArrayData(freshData.students)) {
            studentsObj = studentsArrayToObject(freshData.students);
          } else {
            studentsObj = freshData.students;
          }
        }

        const existingStudent = studentsObj[studentId];

        if (existingStudent) {
          const newCoins = (existingStudent.coins || 0) + amount;
          const newTotalEarned = amount > 0 ? (existingStudent.totalEarned || 0) + amount : (existingStudent.totalEarned || 0);
          const newTotalUsed = amount < 0 ? (existingStudent.totalUsed || 0) + Math.abs(amount) : (existingStudent.totalUsed || 0);

          tx.update(docRef, {
            [`students.${studentId}.coins`]: newCoins,
            [`students.${studentId}.coinsHistory.${historyKey}`]: entryForFirestore,
            [`students.${studentId}.totalEarned`]: newTotalEarned,
            [`students.${studentId}.totalUsed`]: newTotalUsed,
            lastUpdated: serverTimestamp()
          });
        } else {
          const newStudent = {
            studentName,
            studentNumber,
            coins: amount,
            coinsHistory: { [historyKey]: entryForFirestore },
            totalEarned: amount > 0 ? amount : 0,
            totalUsed: amount < 0 ? Math.abs(amount) : 0
          };
          const newTotalStudents = Object.keys(studentsObj).length + 1;
          tx.update(docRef, {
            [`students.${studentId}`]: newStudent,
            totalStudents: newTotalStudents,
            lastUpdated: serverTimestamp()
          });
        }
      });

      console.log(`✅ 멤버 ${studentId}에게 ${amount}코인 지급 완료 (문서: ${targetDocId}, V2 부분 업데이트)`);

      // 데이터 변경 후 캐시 무효화
      this.invalidateCoinDocumentsCache();
    } catch (error) {
      console.error(`❌ 멤버 ${studentId}에게 코인 지급 실패:`, error);
      throw error;
    }
  }

  /**
   * 여러 멤버에게 코인 지급 (V2: 부분 업데이트 - dot notation)
   */
  async grantCoinsToMultipleStudents(
    studentIds: string[], 
    amount: number, 
    memo: string = '코인 지급',
    coinType: 'General' | 'Levelup' | 'Store' | 'Assignment' = 'General'
  ): Promise<void> {
    try {
      console.log(`💰 다중 코인 지급 시작: ${studentIds.length}명, ${amount}코인씩`);

      // V2: 중첩 객체 형식으로 문서 조회
      let documentsV2 = await this.getAllMembersCoinDocumentsV2();

      // 문서가 없으면 첫 번째 문서 생성 (빈 문서로 시작)
      if (documentsV2.length === 0) {
        console.log('📝 MembersCoin 문서가 없음. 새 문서 생성 중...');
        await this.createNewMembersCoinDocument();
        documentsV2 = await this.getAllMembersCoinDocumentsV2();
        console.log('📝 새로 생성된 문서들:', documentsV2.length, '개');
      }

      // 새 학생 추가 시 클래스 멤버 정보 미리 로드
      const studentInfoMap = new Map<string, { studentName: string; studentNumber: string }>();
      try {
        const classMembers = await this.loadClassMembers();
        for (const member of classMembers) {
          studentInfoMap.set(member.studentId, {
            studentName: member.studentName || '',
            studentNumber: member.studentNumber || ''
          });
        }
      } catch (error) {
        console.warn('클래스 멤버 정보 로드 실패 (무시)', error);
      }

      const updatesByDoc = new Map<string, string[]>();
      const lastDocId = documentsV2[documentsV2.length - 1]?.id;

      for (const studentId of studentIds) {
        console.log(`🪙 코인 지급 대상 멤버: ${studentId}`);
        let targetDocId = '';

        for (const doc of documentsV2) {
          if (doc.students[studentId]) {
            targetDocId = doc.id;
            break;
          }
        }

        if (!targetDocId && lastDocId) {
          targetDocId = lastDocId;
        }

        if (!targetDocId) {
          console.warn(`멤버 ${studentId}를 저장할 문서를 찾을 수 없습니다.`);
          continue;
        }

        const list = updatesByDoc.get(targetDocId) || [];
        list.push(studentId);
        updatesByDoc.set(targetDocId, list);
      }

      for (const [docId, ids] of Array.from(updatesByDoc.entries())) {
        const docRef = firebaseDoc(db, this.getCollectionPath(), docId);
        await runTransaction(db, async (tx) => {
          const freshSnap = await tx.get(docRef);
          if (!freshSnap.exists()) {
            throw new Error('MembersCoin 문서를 찾을 수 없습니다.');
          }
          const freshData = freshSnap.data() as any;
          
          // students 필드 처리 (배열/객체 듀얼 지원)
          let studentsObj: { [studentId: string]: StudentCoinInfoV2 } = {};
          if (freshData.students) {
            if (isArrayData(freshData.students)) {
              studentsObj = studentsArrayToObject(freshData.students);
            } else {
              studentsObj = freshData.students;
            }
          }

          // V2: 부분 업데이트 객체 생성 (dot notation)
          const updateObj: { [key: string]: any } = {
            lastUpdated: serverTimestamp()
          };

          let newTotalStudents = Object.keys(studentsObj).length;
          const now = new Date();
          const entryForFirestore = {
            earnedCoin: amount,
            earnedCoinMemo: memo,
            earnedAt: Timestamp.fromDate(now),
            coinType: coinType || 'General'
          };
          const historyKey = getCoinHistoryKey(entryForFirestore);

          for (const studentId of ids) {
            const existingStudent = studentsObj[studentId];

            if (existingStudent) {
              const newCoins = (existingStudent.coins || 0) + amount;
              const newTotalEarned = amount > 0 ? (existingStudent.totalEarned || 0) + amount : (existingStudent.totalEarned || 0);
              const newTotalUsed = amount < 0 ? (existingStudent.totalUsed || 0) + Math.abs(amount) : (existingStudent.totalUsed || 0);
              updateObj[`students.${studentId}.coins`] = newCoins;
              updateObj[`students.${studentId}.coinsHistory.${historyKey}`] = entryForFirestore;
              updateObj[`students.${studentId}.totalEarned`] = newTotalEarned;
              updateObj[`students.${studentId}.totalUsed`] = newTotalUsed;
            } else {
              const studentInfo = studentInfoMap.get(studentId) || { studentName: '', studentNumber: '' };
              updateObj[`students.${studentId}`] = {
                studentName: studentInfo.studentName,
                studentNumber: studentInfo.studentNumber,
                coins: amount,
                coinsHistory: { [historyKey]: entryForFirestore },
                totalEarned: amount > 0 ? amount : 0,
                totalUsed: amount < 0 ? Math.abs(amount) : 0
              };
              newTotalStudents++;
            }
          }

          // totalStudents 업데이트
          updateObj.totalStudents = newTotalStudents;
          updateObj.totalSize = this.calculateDocumentSize({ ...freshData, students: { ...studentsObj, ...Object.fromEntries(ids.map(id => [id, studentsObj[id] || {}])) } });

          tx.update(docRef, updateObj);
        });
      }

      this.invalidateCoinDocumentsCache();

      console.log(`✅ ${studentIds.length}명의 멤버에게 ${amount}코인씩 지급 완료 (V2 부분 업데이트)`);
    } catch (error) {
      console.error('❌ 여러 멤버에게 코인 지급 실패:', error);
      throw error;
    }
  }

  /**
   * 여러 멤버의 마지막 코인 지급 내역 취소 (V2: 객체일 때 deleteField, 배열일 때 전체 교체)
   * @returns 실제로 취소된 멤버 수
   */
  async revokeCoinsFromStudents(
    studentIds: string[],
    amount: number,
    memo: string
  ): Promise<number> {
    try {
      if (studentIds.length === 0) return 0;

      const documentsV2 = await this.getAllMembersCoinDocumentsV2();
      if (documentsV2.length === 0) return 0;

      const updatesByDoc = new Map<string, string[]>();
      const lastDocId = documentsV2[documentsV2.length - 1]?.id;

      for (const studentId of studentIds) {
        let targetDocId = '';
        for (const doc of documentsV2) {
          if (doc.students[studentId]) {
            targetDocId = doc.id;
            break;
          }
        }
        if (!targetDocId && lastDocId) targetDocId = lastDocId;
        if (!targetDocId) continue;
        const list = updatesByDoc.get(targetDocId) || [];
        list.push(studentId);
        updatesByDoc.set(targetDocId, list);
      }

      let revokedCount = 0;

      for (const [docId, ids] of Array.from(updatesByDoc.entries())) {
        const docRef = firebaseDoc(db, this.getCollectionPath(), docId);
        const countInDoc = await runTransaction(db, async (tx) => {
          const freshSnap = await tx.get(docRef);
          if (!freshSnap.exists()) return 0;
          const freshData = freshSnap.data() as any;
          let studentsObj: { [studentId: string]: StudentCoinInfoV2 } = {};
          if (freshData.students) {
            if (isArrayData(freshData.students)) {
              studentsObj = studentsArrayToObject(freshData.students);
            } else {
              studentsObj = freshData.students;
            }
          }
          let count = 0;
          const updatePayload: Record<string, any> = { lastUpdated: serverTimestamp() };

          for (const studentId of ids) {
            const student = studentsObj[studentId];
            if (!student) continue;
            const rawHistory = freshData.students?.[studentId]?.coinsHistory ?? student.coinsHistory;
            const historyArr = coinsHistoryRawToArray(rawHistory);
            if (historyArr.length === 0) continue;
            const lastEntry = historyArr[historyArr.length - 1];
            if (lastEntry.earnedCoin !== amount || lastEntry.earnedCoinMemo !== memo) continue;

            const remaining = historyArr.slice(0, -1);
            const totals = this.calculateCoinTotals(remaining);

            const coinsHistoryStoredAsObject = rawHistory && typeof rawHistory === 'object' && !Array.isArray(rawHistory);
            if (coinsHistoryStoredAsObject) {
              const keyToDelete = getCoinHistoryKey(lastEntry);
              updatePayload[`students.${studentId}.coinsHistory.${keyToDelete}`] = deleteField();
            } else {
              const remainingWithTs = remaining.map(h => ({
                ...normalizeCoinHistoryEntry(h),
                earnedAt: h.earnedAt && typeof (h.earnedAt as any).toMillis === 'function'
                  ? h.earnedAt
                  : Timestamp.fromDate(h.earnedAt instanceof Date ? h.earnedAt : new Date((h.earnedAt as any)?.seconds ? (h.earnedAt as any).toDate() : h.earnedAt))
              })) as CoinHistoryEntry[];
              updatePayload[`students.${studentId}.coinsHistory`] = coinsHistoryArrayToObject(remainingWithTs);
            }
            updatePayload[`students.${studentId}.coins`] = totals.totalCoins;
            updatePayload[`students.${studentId}.totalEarned`] = totals.totalEarned;
            updatePayload[`students.${studentId}.totalUsed`] = totals.totalUsed;
            count++;
          }

          if (count > 0) {
            tx.update(docRef, updatePayload);
          }
          return count;
        });
        revokedCount += countInDoc;
      }

      this.invalidateCoinDocumentsCache();
      return revokedCount;
    } catch (error) {
      console.error('❌ 코인 지급 취소 실패:', error);
      throw error;
    }
  }

  /**
   * 멤버의 코인 데이터 직접 업데이트 (V2: 부분 업데이트 - dot notation)
   */
  async updateStudentCoinData(
    studentId: string,
    coins: number,
    coinsHistory: CoinHistory[]
  ): Promise<void> {
    try {
      console.log(`💰 멤버 코인 데이터 업데이트: ${studentId}, coins: ${coins}`);

      // CoinHistory[] → CoinHistoryEntry[] 변환 (coinType 기본값 설정)
      const coinsHistoryEntries: CoinHistoryEntry[] = coinsHistory.map(h => ({
        earnedCoin: h.earnedCoin,
        earnedCoinMemo: h.earnedCoinMemo,
        earnedAt: h.earnedAt,
        coinType: h.coinType || 'General'
      }));

      // V2: 중첩 객체 형식으로 문서 조회
      let documentsV2 = await this.getAllMembersCoinDocumentsV2();

      if (documentsV2.length === 0) {
        await this.createNewMembersCoinDocument();
        documentsV2 = await this.getAllMembersCoinDocumentsV2();
      }

      let targetDocId = '';
      let targetDocV2: CoinManagementPartitionV2 | null = null;

      for (const doc of documentsV2) {
        if (doc.students[studentId]) {
          targetDocId = doc.id;
          targetDocV2 = doc;
          break;
        }
      }

      if (!targetDocId) {
        const lastDoc = documentsV2[documentsV2.length - 1];
        targetDocId = lastDoc?.id || '';
        targetDocV2 = lastDoc || null;
      }

      if (!targetDocId || !targetDocV2) {
        throw new Error('MembersCoin 문서를 찾을 수 없습니다.');
      }

      const docRef = firebaseDoc(db, this.getCollectionPath(), targetDocId);

      const entriesWithTimestamp: CoinHistoryEntry[] = coinsHistoryEntries.map(h => {
        const earnedAt = h.earnedAt;
        const ts =
          earnedAt && typeof (earnedAt as any).toMillis === 'function'
            ? (earnedAt as any)
            : earnedAt instanceof Date
              ? Timestamp.fromDate(earnedAt)
              : Timestamp.fromDate(new Date((earnedAt as any)?.seconds ? (earnedAt as any).toDate?.() ?? new Date() : earnedAt));
        return { ...h, earnedAt: ts };
      });
      const coinsHistoryObj = coinsHistoryArrayToObject(entriesWithTimestamp);

      await runTransaction(db, async (tx) => {
        const freshSnap = await tx.get(docRef);
        if (!freshSnap.exists()) {
          throw new Error('MembersCoin 문서를 찾을 수 없습니다.');
        }
        const freshData = freshSnap.data() as any;
        
        let studentsObj: { [studentId: string]: StudentCoinInfoV2 } = {};
        if (freshData.students) {
          if (isArrayData(freshData.students)) {
            studentsObj = studentsArrayToObject(freshData.students);
          } else {
            studentsObj = freshData.students;
          }
        }

        const { totalEarned: earned, totalUsed: used } = this.calculateCoinTotals(coinsHistoryEntries);
        const existingStudent = studentsObj[studentId];

        if (existingStudent) {
          tx.update(docRef, {
            [`students.${studentId}.coins`]: coins,
            [`students.${studentId}.coinsHistory`]: coinsHistoryObj,
            [`students.${studentId}.totalEarned`]: earned,
            [`students.${studentId}.totalUsed`]: used,
            lastUpdated: serverTimestamp()
          });
        } else {
          let studentName = '';
          let studentNumber = '';
          try {
            const classMembers = await this.loadClassMembers();
            const member = classMembers.find((m: any) => m.studentId === studentId);
            if (member) {
              studentName = member.studentName || '';
              studentNumber = member.studentNumber || '';
            }
          } catch (error) {
            console.warn(`클래스 멤버 정보 로드 실패 (무시): ${studentId}`, error);
          }

          const newStudentForFirestore = {
            studentName,
            studentNumber,
            coins,
            coinsHistory: coinsHistoryObj,
            totalEarned: earned,
            totalUsed: used
          };

          const newTotalStudents = Object.keys(studentsObj).length + 1;
          tx.update(docRef, {
            [`students.${studentId}`]: newStudentForFirestore,
            totalStudents: newTotalStudents,
            lastUpdated: serverTimestamp()
          });
        }
      });

      this.invalidateCoinDocumentsCache();

      console.log(`✅ 멤버 ${studentId} 코인 데이터 업데이트 완료 (V2 부분 업데이트)`);
    } catch (error) {
      console.error(`❌ 멤버 ${studentId} 코인 데이터 업데이트 실패:`, error);
      throw error;
    }
  }

  private calculateCoinTotals(history: CoinHistory[]): {
    totalCoins: number;
    totalEarned: number;
    totalUsed: number;
  } {
    const totalCoins = history.reduce((sum, h) => sum + (h.earnedCoin || 0), 0);
    const totalEarned = history
      .filter(h => h.earnedCoin > 0)
      .reduce((sum, h) => sum + h.earnedCoin, 0);
    const totalUsed = history
      .filter(h => h.earnedCoin < 0)
      .reduce((sum, h) => sum + Math.abs(h.earnedCoin), 0);
    return { totalCoins, totalEarned, totalUsed };
  }

  /**
   * 모든 멤버의 코인 요약 정보 조회
   */
  async getAllStudentsCoinSummary(): Promise<{ [studentId: string]: StudentCoinSummary }> {
    try {
      const documents = await this.getAllMembersCoinDocuments();
      const summary: { [studentId: string]: StudentCoinSummary } = {};

      for (const doc of documents) {
        for (const student of doc.students) {
          summary[student.studentId] = {
            studentId: student.studentId,
            studentName: student.studentName,
            studentNumber: student.studentNumber,
            currentCoins: student.coins || 0,
            totalEarned: student.totalEarned || 0,
            totalUsed: student.totalUsed || 0,
            historyCount: student.coinsHistory?.length || 0
          };
        }
      }

      return summary;
    } catch (error) {
      console.error('❌ 멤버 코인 요약 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 기존 ClassMembers에서 코인 데이터 마이그레이션
   */
  async migrateFromClassMembers(): Promise<void> {
    try {
      console.log('🔄 ClassMembers에서 코인 데이터 마이그레이션 시작...');
      console.log(`  userId: ${this.userId}, classId: ${this.classId}`);

      // userDataService를 사용하여 기존 데이터 로드 (coinsHistory 포함)
      const { userDataService } = await import('./userDataService');
      const members = await userDataService.loadClassMembers(this.userId, this.classId, this.memberGroup);

      if (members.length === 0) {
        console.log('⚠️ 마이그레이션할 멤버가 없습니다.');
        return;
      }

      console.log(`📊 마이그레이션 대상 멤버 수: ${members.length}명`);
      members.forEach((m: any) => {
        console.log(`  - ${m.name}: coins=${m.coins || 0}, historyCount=${m.coinsHistory?.length || 0}`);
      });

      // 새 MembersCoin 문서 생성
      let documents = await this.getAllMembersCoinDocuments();
      if (documents.length === 0) {
        await this.createNewMembersCoinDocument();
        documents = await this.getAllMembersCoinDocuments();
      }

      // 각 멤버의 코인 데이터 마이그레이션
      const targetDoc = documents[0];
      targetDoc.students = members.map((member: any) => {
        const coinsHistory = (member.coinsHistory || []).map((h: any) => {
          let earnedAtTimestamp;
          if (h.earnedAt instanceof Date) {
            earnedAtTimestamp = Timestamp.fromDate(h.earnedAt);
          } else if (h.earnedAt && typeof h.earnedAt.toDate === 'function') {
            earnedAtTimestamp = h.earnedAt;
          } else if (h.earnedAt) {
            earnedAtTimestamp = Timestamp.fromDate(new Date(h.earnedAt));
          } else {
            earnedAtTimestamp = Timestamp.now();
          }

          return {
            id: h.id || `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
            earnedCoin: h.earnedCoin || 0,
            earnedCoinMemo: h.earnedCoinMemo || '',
            earnedAt: earnedAtTimestamp,
            coinType: h.coinType || 'General'
          };
        });

        const totalEarned = coinsHistory
          .filter((h: any) => h.earnedCoin > 0)
          .reduce((sum: number, h: any) => sum + h.earnedCoin, 0);
        const totalUsed = coinsHistory
          .filter((h: any) => h.earnedCoin < 0)
          .reduce((sum: number, h: any) => sum + Math.abs(h.earnedCoin), 0);
        const coins = totalEarned - totalUsed;

        return {
          studentId: member.id,
          studentName: member.name || '',
          studentNumber: member.number || '',
          coins: coins,
          coinsHistory: coinsHistory,
          totalEarned: totalEarned,
          totalUsed: totalUsed
        };
      });

      targetDoc.totalStudents = targetDoc.students.length;

      console.log(`📝 저장할 문서:`, {
        id: targetDoc.id,
        classId: targetDoc.classId,
        totalStudents: targetDoc.totalStudents,
        collectionPath: this.getCollectionPath()
      });
      console.log(`📝 첫 번째 멤버 데이터 샘플:`, targetDoc.students[0]);

      // 저장
      const docRef = firebaseDoc(db, this.getCollectionPath(), targetDoc.id);
      console.log(`📝 저장 경로: ${this.getCollectionPath()}/${targetDoc.id}`);
      
      const saveData = {
        id: targetDoc.id,
        classId: targetDoc.classId,
        documentNumber: targetDoc.documentNumber,
        students: targetDoc.students,
        totalStudents: targetDoc.totalStudents,
        totalSize: this.calculateDocumentSize(targetDoc),
        lastUpdated: serverTimestamp()
      };
      
      console.log(`📝 저장 데이터 크기: ${JSON.stringify(saveData).length} bytes`);
      console.log(`📝 저장할 students 배열 길이: ${targetDoc.students.length}`);
      console.log(`📝 첫 번째 멤버 상세:`, JSON.stringify(targetDoc.students[0], null, 2));
      
      // setDoc으로 완전히 덮어쓰기 (merge 옵션 없이)
      await setDoc(docRef, saveData, { merge: false });
      
      // 저장 후 확인
      const savedDoc = await getDoc(docRef);
      if (savedDoc.exists()) {
        const savedData = savedDoc.data();
        console.log(`✅ 저장 확인: 문서 존재, students 수: ${savedData.students?.length || 0}`);
        if (savedData.students && savedData.students.length > 0) {
          console.log(`✅ 저장된 첫 번째 멤버:`, savedData.students[0]);
        }
      } else {
        console.error(`❌ 저장 실패: 문서가 존재하지 않음`);
      }

      console.log(`✅ 마이그레이션 완료: ${targetDoc.students.length}명의 코인 데이터`);
      console.log(`✅ 저장 경로: ${this.getCollectionPath()}/${targetDoc.id}`);
      
      // ClassMembers에서 코인 데이터 삭제
      console.log('🗑️ ClassMembers에서 코인 데이터 삭제 시작...');
      await this.removeCoinsFromClassMembers(members);
      console.log('✅ ClassMembers 코인 데이터 삭제 완료');
      
    } catch (error) {
      console.error('❌ 코인 데이터 마이그레이션 실패:', error);
      throw error;
    }
  }

  /**
   * ClassMembers에서 coins, coinsHistory 필드 삭제
   */
  private async removeCoinsFromClassMembers(members: any[]): Promise<void> {
    try {
      // ClassMembers 파티션 문서들 직접 업데이트
      const classMembersPath = `users/${this.userId}/classes/${this.classId}/ClassMembers`;
      const collectionRef = collection(db, classMembersPath);
      const snapshot = await getDocs(collectionRef);
      
      console.log(`📝 ClassMembers 문서 수: ${snapshot.docs.length}`);
      
      const groupRegex =
        this.memberGroup === 'A'
          ? /^ClassMembers-(\d+)$/
          : new RegExp(`^ClassMembers${this.memberGroup}-(\\d+)$`);

      for (const docSnapshot of snapshot.docs) {
        // 다른 멤버 그룹의 파티션 문서는 건드리지 않음
        if (!groupRegex.test(docSnapshot.id)) continue;

        const docData = docSnapshot.data();
        const membersArray = docData.members || [];
        
        console.log(`📝 문서 ${docSnapshot.id}: ${membersArray.length}명의 멤버`);
        
        // 각 멤버에서 coins, coinsHistory 제거
        const updatedMembers = membersArray.map((member: any) => {
          const { coins, coinsHistory, ...rest } = member;
          return rest;
        });
        
        // 문서 업데이트
        const docRef = firebaseDoc(db, classMembersPath, docSnapshot.id);
        await updateDoc(docRef, {
          members: updatedMembers
        });
        
        console.log(`✅ 문서 ${docSnapshot.id} 업데이트 완료`);
      }
      
      console.log(`✅ ClassMembers에서 코인 데이터 삭제 완료`);
    } catch (error) {
      console.error('❌ ClassMembers 코인 데이터 삭제 실패:', error);
      throw error;
    }
  }
}

export default CoinManagementService;

